import { smoothScroll } from './smooth-scroll';

export function initTabs() : void {
    // @deprecated Utiliser la classe Tabs à la place
    document.querySelectorAll<HTMLElement>('.js-tab').forEach($tab => {
        new Tab($tab);
    });

    document.querySelectorAll<HTMLElement>('.tabs[role="tablist"]').forEach($tabs => {
        new Tabs($tabs);
    });
}

/**
 * @deprecated Utiliser la classe Tabs à la place
 * 
 * @property {HTMLElement}            $tab              The tab container
 * @property {boolean}                withSmoothScroll  If apply a smooth scroll when tab change
 * @property {NodeList.<HTMLElement>} $buttons          All tabs button
 * @property {NodeList.<HTMLElement>} $contents         All tabs content
 */
class Tab {
    $tab: HTMLElement;
    withSmoothScroll: boolean;
    $buttons: NodeListOf<HTMLElement>;
    $contents: NodeListOf<HTMLElement>;

    constructor($tab: HTMLElement) {
        this.$tab = $tab;
        this.withSmoothScroll = typeof $tab.dataset.tabSmoothScroll === 'undefined' || ['true', ''].includes($tab.dataset.tabSmoothScroll);
        this.$buttons = $tab.querySelectorAll<HTMLElement>('.js-tab-button');
        this.$contents = $tab.querySelectorAll<HTMLElement>('.js-tab-content');

        this.$buttons.forEach(($button, index) => {
            if ($button.getAttribute('type') === 'radio') {
                $button.addEventListener('change', e => {
                    if (($button as HTMLInputElement).checked) {
                        this.changeTab((e.target as HTMLElement).dataset.tabName as string);
                    }
                });

                if (($button as HTMLInputElement).checked) {
                    this.changeTab($button.dataset.tabName as string);
                }
            } else {
                $button.addEventListener('click', e => {
                    this.changeTab((e.target as HTMLElement).dataset.tabName as string);
                });

                if (0 === index) {
                    $button.click(); // Initialize first Tab
                }
            }
        });

        if (Array.from(this.$buttons).filter($button => $button.classList.contains('tab-active')).length === 0) {
            this.$buttons[0].click();
        }
    }

    /**
     * Change tab
     * 
     * @param {string} tabName 
     */
    changeTab(tabName: string) : void {
        this.$contents.forEach($content => {
            if (tabName === $content.dataset.tabName) {
                $content.classList.add('tab-active');

                if (this.$tab.dataset.tabHiddenClass) {
                    $content.classList.remove(this.$tab.dataset.tabHiddenClass);
                }

                if (this.withSmoothScroll) {
                    smoothScroll($content, true);
                }
            } else {
                $content.classList.remove('tab-active');

                if (this.$tab.dataset.tabHiddenClass) {
                    $content.classList.add(this.$tab.dataset.tabHiddenClass);
                }
            }
        });

        this.$buttons.forEach($button => {
            if (tabName === $button.dataset.tabName) {
                $button.classList.add('tab-active');
            } else {
                $button.classList.remove('tab-active');
            }
        });
    }
}

type TabType = {
    active: boolean,
    $toggler: HTMLButtonElement,
    $section: HTMLElement,
};

/**
 * Classe permettant de créer un système d'onglets.
 * 
 * Usage:
 * 
 * <div class="tabs" role="tablist">
 *     <button id="{BUTTON_1_ID}" type="button" role="tab" aria-selected="{true|false}" aria-controls="{SECTION_1_ID}">{BUTTON_1_LABEL}</button>
 *     <button id="{BUTTON_2_ID}" type="button" role="tab" aria-selected="{true|false}" aria-controls="{SECTION_2_ID}">{BUTTON_2_LABEL}</button>
 * </div>
 * 
 * <div id="{SECTION_1_ID}" role="tabpanel" aria-labelledby="{BUTTON_1_ID}">{SECTION_1_CONTENT}</div>
 * <div id="{SECTION_2_ID}" role="tabpanel" aria-labelledby="{BUTTON_2_ID}" class="hidden">{SECTION_2_CONTENT}</div>
 */
class Tabs {
    tabs: TabType[] = [];

    constructor($tabs: HTMLElement) {
        $tabs.querySelectorAll<HTMLButtonElement>('[role="tab"]').forEach($btn => {
            const tabId = $btn.getAttribute('aria-controls') as string;
            const $section = (tabId ? document.getElementById(tabId) : null) as HTMLElement;

            if (!$section) {
                return;
            }

            this.tabs.push({
                active: 'true' === $btn.getAttribute('aria-selected'),
                $toggler: $btn,
                $section,
            });
        });

        this.tabs.forEach((tab: TabType, index: number) => {
            // Navigation entre les onglets
            tab.$toggler.addEventListener('keydown', evt => {
                let toFocus: HTMLButtonElement|null = null;

                if ('ArrowLeft' === evt.key) {
                    const prevTab = this.tabs[index - 1] ?? this.tabs[this.tabs.length - 1];
                    toFocus = prevTab.$toggler;
                } else if ('ArrowRight' === evt.key) {
                    const nextTab = this.tabs[index + 1] ?? this.tabs[0];
                    toFocus = nextTab.$toggler;
                } else if ('Home' === evt.key) {
                    toFocus = this.tabs[0].$toggler;
                } else if ('End' === evt.key) {
                    toFocus = this.tabs[this.tabs.length - 1].$toggler;
                } else if (['Enter', 'Space'].includes(evt.key)) {
                    this.changeTab(tab);
                }

                if (toFocus) {
                    evt.preventDefault();
                    toFocus.focus();
                }
            });

            // Change d'onglet au click sur l'un d'eux
            tab.$toggler.addEventListener('click', () => {
                this.changeTab(tab);
            });
        });

        // On force le changement de tab sur celui qui est censé être actif (ou sur le premier
        // s'il n'y en a pas) pour s'assurer que le HTML est bien initialisé
        const activeTab = this.tabs.filter(tab => tab.active)[0] || this.tabs[0];
        this.changeTab(activeTab);
    }

    /**
     * Change d'onglet
     * 
     * @param {TabType} currentTab
     */
    changeTab(currentTab: TabType): void {
        this.tabs.forEach(tab => {
            if (currentTab !== tab) {
                tab.$toggler.setAttribute('aria-selected', 'false');
                tab.$toggler.setAttribute('tabindex', '-1');

                tab.$section.classList.add('hidden');
                tab.$section.setAttribute('aria-hidden', 'true');
            } else {
                tab.$toggler.setAttribute('aria-selected', 'true');
                tab.$toggler.removeAttribute('tabindex');

                tab.$section.classList.remove('hidden');
                tab.$section.removeAttribute('aria-hidden');
            }
        });
    }
}
